import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Flex, Icon, UseDisclosureProps, Box } from '@chakra-ui/react';
import { CustomReportTilesLayout } from '@/pages/reports/custom-reports/components';
import { ReportTypes } from '../../types';
import { Button } from '@kula-ai/components';
import { PlusCircle } from '@kula-ai/icons-core';
import { KText } from '@/ui-library/components';
import { ReportEmpty } from '@/assets/icons/ats';
import { generateBackground } from '@/components/resizable-grid/utils';

interface ReportBodyProps {
  reportData: ReportTypes.ReportMeta;
  isPrintView?: boolean;
  isEditMode?: boolean;
  onRemoveBlock?: (blockId: string) => void;
  onLayoutChange?: (newLayout: any[]) => void;
  isLocalUpdate?: boolean;
  showAddBlockDisclosure?: UseDisclosureProps;
  gridColor?: string;
  layoutResetKey?: number;
  onTileMetaChange?: (tileId: string, metadata: any) => void;
  getCurrentMergedData?: () => any;
}

export interface ReportBodyRef {
  scrollToNewBlocks: (
    newBlockPositions: {
      key: string;
      subject_type?: string;
      x: number;
      y: number;
      w: number;
      h: number;
    }[]
  ) => void;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

export const ReportBody = forwardRef<ReportBodyRef, ReportBodyProps>(
  (
    {
      reportData,
      isPrintView,
      isEditMode,
      onRemoveBlock,
      onLayoutChange,
      isLocalUpdate,
      showAddBlockDisclosure,
      layoutResetKey = 0,
      onTileMetaChange,
      getCurrentMergedData,
    },
    ref
  ) => {
    const { layoutData, isStaticReport } = reportData;
    const { tiles } = layoutData ?? {};
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [gridDimensions, setGridDimensions] = useState({
      width: 0,
      height: 0,
    });

    const isEmptyReport = tiles?.length === 0;

    // Expose scroll functionality to parent
    useImperativeHandle(
      ref,
      () => ({
        scrollContainerRef,
        scrollToNewBlocks: (
          newBlockPositions: {
            key: string;
            subject_type?: string;
            x: number;
            y: number;
            w: number;
            h: number;
          }[]
        ) => {
          if (newBlockPositions.length === 0) return;

          const firstNewBlock = newBlockPositions[0];

          // Function to try finding and scrolling to the element
          const tryScrollToElement = (attempts = 0) => {
            const maxAttempts = 10;

            // Try different element selectors
            // The DOM element ID is constructed as `${subject_type}-${id}` in ReportTileContainer
            const selectors = [
              `#${firstNewBlock.key}`, // Direct ID match (temp_123456)
              `[id*="${firstNewBlock.key}"]`, // ID contains the key (flow_health-temp_123456)
              `[data-tile-key="${firstNewBlock.key}"]`, // Data attribute
              `.tile-item-container:last-child`, // Last tile container as fallback
            ];

            // If we have subject_type, construct the exact DOM ID
            if (firstNewBlock.subject_type) {
              const exactId = `${firstNewBlock.subject_type}-${firstNewBlock.key}`;
              selectors.unshift(`#${exactId}`); // Exact match: flow_health-temp_123456
            } else if (firstNewBlock.key.includes('temp_')) {
              // Fallback for flow components when subject_type is not available
              selectors.unshift(
                `#flow_health-${firstNewBlock.key}`, // flow_health-temp_123456
                `#flow_user_activity-${firstNewBlock.key}`, // flow_user_activity-temp_123456
                `#flow_candidate_touchpoint-${firstNewBlock.key}`, // flow_candidate_touchpoint-temp_123456
                `[id$="-${firstNewBlock.key}"]` // Any ID ending with -temp_123456
              );
            }

            let targetElement: HTMLElement | null = null;

            for (const selector of selectors) {
              try {
                targetElement = document.querySelector(selector);
                if (targetElement) {
                  break;
                }
              } catch (e) {
                // Invalid selector, continue
              }
            }

            if (targetElement) {
              // Get scroll container
              const scrollContainer = scrollContainerRef.current;
              if (!scrollContainer) {
                return;
              }

              // Calculate scroll position
              const containerRect = scrollContainer.getBoundingClientRect();
              const elementRect = targetElement.getBoundingClientRect();
              const scrollTop = scrollContainer.scrollTop;

              // Calculate the target scroll position (element top relative to container)
              const targetScrollTop =
                scrollTop + elementRect.top - containerRect.top - 100; // 100px offset from top

              scrollContainer.scrollTo({
                top: Math.max(0, targetScrollTop),
                behavior: 'smooth',
              });

              return;
            }

            // If not found and we haven't exceeded max attempts, try again
            if (attempts < maxAttempts) {
              setTimeout(() => tryScrollToElement(attempts + 1), 100);
            } else {
              // Fallback: scroll to bottom of container
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                  top: scrollContainerRef.current.scrollHeight,
                  behavior: 'smooth',
                });
              }
            }
          };

          // Start trying to find the element
          tryScrollToElement();
        },
      }),
      []
    );

    useEffect(() => {
      if (!isEmptyReport) return;

      const updateDimensions = () => {
        if (containerRef.current) {
          setGridDimensions({
            width: containerRef.current.clientWidth,
            height: containerRef.current.clientHeight,
          });
        }
      };

      updateDimensions();
      const observer = new ResizeObserver(updateDimensions);

      if (containerRef.current) {
        observer.observe(containerRef.current);
      }

      return () => observer.disconnect();
    }, [isEmptyReport]);

    if (isEmptyReport) {
      const gridBackground =
        gridDimensions.width > 0
          ? generateBackground({
              gridWidth: gridDimensions.width,
              gridHeight: gridDimensions.height,
              rowHeight: 10,
              columns: 12,
              margin: 12,
              isEditing: true,
              gridColor: '#F8F9FC',
            })
          : '';

      return (
        <Box
          ref={containerRef}
          h="100%"
          position="relative"
          m="24px 16px 0 16px"
          style={{
            backgroundImage: gridBackground,
            backgroundPosition: '0px 0px',
            backgroundSize: '100%',
          }}
        >
          <Flex
            w="100%"
            h="90%"
            alignItems="center"
            flexDir="column"
            justifyContent="center"
          >
            <Icon mb="-24px" as={ReportEmpty} />
            <KText variant="heading_4">No blocks added</KText>
            <KText mt="6px" mb="12px">
              Looks like there are no blocks in this report. Go ahead, add one!
            </KText>
            <Button
              height={'34px'}
              onClick={showAddBlockDisclosure?.onOpen}
              variant={'primary'}
              leftElement={<PlusCircle size={16} color="!text-primary-500" />}
            >
              Add block
            </Button>
          </Flex>
        </Box>
      );
    }

    return (
      <Flex
        ref={scrollContainerRef}
        direction="column"
        overflowY="scroll"
        flex="1"
        px={isPrintView ? '0' : '24px'}
        my="16px"
      >
        <CustomReportTilesLayout
          key={`report-tiles-${layoutResetKey}`}
          customReport={layoutData}
          isStaticReport={isStaticReport}
          onLayoutChange={onLayoutChange}
          isEditMode={isEditMode}
          isPrintView={isPrintView}
          isReadOnly={isPrintView}
          onRemoveBlock={onRemoveBlock}
          onTileMetaChange={isEditMode ? onTileMetaChange : undefined}
          getCurrentMergedData={getCurrentMergedData}
        />
      </Flex>
    );
  }
);

ReportBody.displayName = 'ReportBody';
